import { ListingRequestCard } from 'app/components/listing-request-card';
import { MultipleOfferCard } from 'app/components/multiple-offer-card';
import { RequestEmptyState } from 'app/components/request-empty-state';
import type { Listing } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import React from 'react';
import { Strategies } from '../../all-listings/components/recommended-listings';
import { DesktopRecommendationSection } from '../../all-listings/components/recommended-listings.large';
import { DesktopHeader } from '../desktop-header';
import type { useDesktopLandingData } from '../use-desktop-landing-data';
import { CountBadge } from './count-badge';
import { LandingCategories } from './landing-categories';
import { TrialBanner } from './trial-banner';
import { UpcomingActivity } from './upcoming-activity';
import { UnlockFullExperienceCard } from './unlock-full-experience-card';
import { ProfileCompletionSteps, useHasPartagePlusSubscription } from '../shared';

interface LandingPageSection {
  key: string;
  node: React.ReactElement;
}

export function LandingOverviewSections({ data }: { data: ReturnType<typeof useDesktopLandingData> }) {
  const hasPartagePlusSubscription = useHasPartagePlusSubscription();
  const {
    profile,
    user,
    stats,
    unreadData,
    isLoggedIn,
    freeSubAvailable,
    freeSubNumDaysLeft,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    completeProfilePromptCompleted,
    canViewActivitySummary,
    lendings,
    borrowings,
    loansQuerySettled,
    visibleExpiredLoans,
    navigation,
    actions,
    hideStepsToComplete,
    completeProfileSteps,
    showTopMultipleOfferCard,
    dismissTopMultipleOfferCard,
    urgentNeedsRecommendations,
    urgentNeedsCount,
    recentOffersRecommendations,
    shouldShowRecommendationSection,
  } = data;
  const items: LandingPageSection[] = [
    {
      key: 'header',
      node: (
        <DesktopHeader
          activitySubtitle={activitySubtitle}
          addressLabel={addressLabel}
          greeting={greeting}
          isLoggedIn={isLoggedIn}
          navigation={navigation}
          profile={profile}
          statsLoaded={Boolean(stats && unreadData)}
          hasLateLoans={visibleExpiredLoans.length > 0}
          unreadCount={unreadCount}
          user={user}
        />
      ),
    },
    ...(isLoggedIn && !completeProfilePromptCompleted && !hideStepsToComplete
      ? [{ key: 'steps-to-complete', node: <ProfileCompletionSteps {...completeProfileSteps} /> }]
      : []),
    ...(showTopMultipleOfferCard
      ? [
          {
            key: 'multiple-offer-card-top',
            node: <MultipleOfferCard onPublish={dismissTopMultipleOfferCard} />,
          },
        ]
      : []),
    ...(freeSubAvailable && typeof freeSubNumDaysLeft === 'number'
      ? [
          {
            key: 'trial-banner',
            node: (
              <TrialBanner
                freeSubAvailable={freeSubAvailable}
                freeSubNumDaysLeft={freeSubNumDaysLeft}
                onSubscribe={actions.openMyPlan}
              />
            ),
          },
        ]
      : []),
    ...(isLoggedIn && canViewActivitySummary
      ? [
          {
            key: 'upcoming-activity',
            node: (
              <UpcomingActivity
                borrowings={borrowings ?? []}
                isLoading={!loansQuerySettled}
                lendings={lendings ?? []}
                onViewAll={actions.openManagementOverview}
                variant="large"
              />
            ),
          },
        ]
      : []),
    ...(shouldShowRecommendationSection(urgentNeedsRecommendations, { hasEmptyState: true })
      ? [
          {
            key: 'urgent-needs',
            node: (
              <DesktopRecommendationSection<Listing>
                strategyProps={{ strategy: Strategies.UrgentNeeds }}
                recommendationState={urgentNeedsRecommendations}
                recommendations={urgentNeedsRecommendations.recommendations as Listing[] | undefined}
                onViewAll={actions.openRequests}
                renderItem={(listing) => <ListingRequestCard listing={listing} showUser />}
                emptyState={<RequestEmptyState onPressViewAll={actions.openRequests} />}
                titleSuffix={<CountBadge count={urgentNeedsCount} />}
              />
            ),
          },
        ]
      : []),
    ...(hasPartagePlusSubscription === false
      ? [
          {
            key: 'unlock-full-experience',
            node: <UnlockFullExperienceCard onPress={actions.openPayment} variant="large" />,
          },
        ]
      : []),
    ...(shouldShowRecommendationSection(recentOffersRecommendations)
      ? [
          {
            key: 'recent-offers',
            node: (
              <DesktopRecommendationSection<Listing>
                strategyProps={{ strategy: Strategies.RecentOffers }}
                recommendationState={recentOffersRecommendations}
                recommendations={recentOffersRecommendations.recommendations as Listing[] | undefined}
                onViewAll={actions.openExploreOffers}
              />
            ),
          },
        ]
      : []),
    ...(isWhiteLabel(WhiteLabel.HauteGaspesie)
      ? [
          {
            key: 'categories',
            node: (
              <LandingCategories
                onTagPress={actions.openOfferTag}
                onViewAll={actions.openExploreOffers}
                variant="large"
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {items.map((section) => (
        <React.Fragment key={section.key}>{section.node}</React.Fragment>
      ))}
    </>
  );
}
